import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Clock, Loader2, Pencil, Info } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errMsg } from "@/lib/api";
import { EASE } from "@/components/motion/Motion";
import { FIELDS, StepAbout, StepResume, StepQuestions } from "@/pages/careers/ApplySteps";
import { NoticePanel, useNotice } from "@/components/privacy/Notice";

const STEPS = ["About you", "Resume", "Questions", "Review"];
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function validate(step, { f, file, ko, custom, job }) {
  const e = {};
  if (step === 0) {
    if (!f.name.trim()) e.name = "Enter your full name.";
    if (!EMAIL.test(f.email.trim())) e.email = "Enter a valid email address.";
  }
  if (step === 1) {
    if (!file) e.resume = "Please attach your resume.";
    else if (file.size > 10 * 1024 * 1024) e.resume = "Your resume must be under 10 MB.";
    else if (!/\.(pdf|docx?|DOCX?|PDF)$/.test(file.name)) e.resume = "Upload a PDF, DOCX or DOC file.";
  }
  if (step === 2) {
    job.knockout_questions.forEach((q, i) => { if (!ko[q]) e[`ko-${i}`] = "Please choose yes or no."; });
    job.custom_questions.forEach((q) => { if (q.required && !(custom[q.id] || "").trim()) e[`c-${q.id}`] = "This question is required."; });
  }
  return e;
}

const Row = ({ label, value }) => <div className="flex gap-3 py-1.5 text-sm"><span className="w-28 shrink-0 text-ink-muted">{label}</span><span className="min-w-0 flex-1 break-words text-ink">{value || "—"}</span></div>;

function ConsentBox({ id, checked, onChange, children, required }) {
  return (
    <label className="flex items-start gap-3 text-sm text-ink-secondary" htmlFor={id}>
      <Checkbox id={id} checked={checked} onCheckedChange={(v) => onChange(!!v)} className="mt-0.5" data-testid={id} />
      <span>{children}{required && <span className="ml-1 text-risk" aria-label="required">*</span>}</span>
    </label>
  );
}

function Review({ s, job, go, consent, setConsent, slug, lang, setLang, cons, setCons }) {
  const { data: notice } = useNotice(`/public/careers/${slug}/notice`, lang);
  const set = (k) => (v) => setCons((c) => ({ ...c, [k]: v }));
  const Section = ({ i, children }) => (
    <section className="rounded-[6px] border border-line p-4">
      <div className="mb-1 flex items-center justify-between"><h3 className="text-sm font-semibold">{STEPS[i]}</h3>
        <button type="button" onClick={() => go(i)} className="inline-flex items-center gap-1 text-xs font-medium text-brand hover:underline" data-testid={`apply-edit-step-${i}`}><Pencil size={12} /> Edit</button></div>
      {children}
    </section>
  );
  return (
    <div className="space-y-4" data-testid="apply-review">
      <Section i={0}>{FIELDS.map(([k, l]) => <Row key={k} label={l} value={s.f[k]} />)}</Section>
      <Section i={1}><Row label="File" value={s.file?.name} /></Section>
      <Section i={2}>{job.knockout_questions.map((q) => <Row key={q} label="Yes/No" value={`${q} ${s.ko[q] ? `— ${s.ko[q]}` : ""}`} />)}
        {job.custom_questions.map((q) => <Row key={q.id} label="Answer" value={`${q.question}: ${s.custom[q.id] || "—"}`} />)}
        <Row label="Equal opportunity" value={Object.values(s.demo).filter(Boolean).length ? "Answered (kept separately)" : "Skipped"} /></Section>
      <p className="flex gap-2 rounded-[5px] bg-subtle px-4 py-3 text-xs leading-relaxed text-ink-secondary" data-tour="pt-ai-disclosure" data-testid="ai-disclosure"><Info size={14} className="mt-0.5 shrink-0" aria-hidden="true" /> {job.ai_disclosure}</p>
      <NoticePanel url={`/public/careers/${slug}/notice`} lang={lang} setLang={setLang} />
      {notice && (
        <div className="space-y-3" data-testid="apply-consents">
          <ConsentBox id="apply-consent-checkbox" checked={consent} onChange={setConsent} required>{notice.labels.recruitment}</ConsentBox>
          <ConsentBox id="apply-age-checkbox" checked={cons.age} onChange={set("age")} required>{notice.labels.age}</ConsentBox>
          <ConsentBox id="apply-ai-consent-checkbox" checked={cons.ai} onChange={set("ai")}>{notice.labels.ai_screening}</ConsentBox>
          <ConsentBox id="apply-future-jobs-checkbox" checked={cons.future} onChange={set("future")}>{notice.labels.future_jobs}</ConsentBox>
          <div className="flex flex-wrap items-center gap-2 pl-7 text-sm">
            <label htmlFor="apply-dob" className="text-ink-secondary">Date of birth (optional)</label>
            <input id="apply-dob" type="date" value={cons.dob} onChange={(e) => set("dob")(e.target.value)} className="h-9 rounded-lg border border-line bg-white px-2" data-testid="apply-dob-input" />
          </div>
          <p className="pl-7 text-xs text-ink-muted"><Link to="/privacy" target="_blank" className="underline" data-testid="apply-privacy-link">Privacy policy</Link> · you can withdraw consent at any time from the link we email you.</p>
        </div>
      )}
    </div>
  );
}

export function ApplyForm({ slug, job, color, onDone }) {
  const [step, setStep] = useState(0);
  const [dir, setDir] = useState(1);
  const [f, setF] = useState({ name: "", email: "", phone: "", location: "", linkedin: "" });
  const [ko, setKo] = useState({});
  const [custom, setCustom] = useState({});
  const [file, setFile] = useState(null);
  const [demo, setDemo] = useState({});
  const [consent, setConsent] = useState(false);
  const [cons, setCons] = useState({ age: false, ai: false, future: false, dob: "" });
  const [lang, setLang] = useState("en");
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const s = { f, file, ko, custom, demo, job };
  const go = (n) => { setDir(n > step ? 1 : -1); setErrors({}); setStep(n); };
  const next = () => { const e = validate(step, s); setErrors(e); if (Object.keys(e).length) return; go(step + 1); };

  const submit = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(f).forEach(([k, v]) => fd.append(k, v));
      fd.append("answers", JSON.stringify({ knockout: ko, custom }));
      fd.append("consent", String(consent));
      fd.append("consent_version", job.consent_version);
      fd.append("consent_ai_screening", String(cons.ai));
      fd.append("consent_future_jobs", String(cons.future));
      fd.append("age_confirmed", String(cons.age));
      fd.append("dob", cons.dob);
      fd.append("notice_lang", lang);
      fd.append("resume", file);
      fd.append("demographics", JSON.stringify(demo));
      const { data } = await api.post(`/public/careers/${slug}/apply`, fd);
      onDone(data);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <section className="panel space-y-5 p-5 sm:p-8" data-testid="apply-form">
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-xl font-semibold">Apply for this role</h2>
          <span className="inline-flex items-center gap-1 text-xs text-ink-muted"><Clock size={12} aria-hidden="true" /> About 3 minutes</span>
        </div>
        <div className="h-1.5 overflow-hidden rounded-full bg-[#EFEBE3]" role="progressbar" aria-valuenow={step + 1} aria-valuemin={1} aria-valuemax={4} data-testid="apply-progress">
          <motion.div className="h-full rounded-full" style={{ background: color }} animate={{ width: `${((step + 1) / STEPS.length) * 100}%` }} transition={{ duration: 0.3, ease: EASE }} />
        </div>
        <ol className="grid grid-cols-4 gap-1 text-[11px] sm:text-xs" data-tour="pt-apply-step-indicator" data-testid="apply-step-indicator">
          {STEPS.map((l, i) => <li key={l} className={i === step ? "font-semibold text-ink" : i < step ? "text-ink-secondary" : "text-ink-muted"} aria-current={i === step ? "step" : undefined}>{i + 1}. {l}</li>)}
        </ol>
      </div>
      <AnimatePresence mode="wait" initial={false} custom={dir}>
        <motion.div key={step} initial={{ opacity: 0, x: 16 * dir }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 * dir }} transition={{ duration: 0.2, ease: EASE }} data-testid={`apply-step-${step}`}>
          {step === 0 && <StepAbout f={f} setF={setF} errors={errors} />}
          {step === 1 && <StepResume file={file} setFile={setFile} color={color} error={errors.resume} />}
          {step === 2 && <StepQuestions job={job} ko={ko} setKo={setKo} custom={custom} setCustom={setCustom} demo={demo} setDemo={setDemo} errors={errors} />}
          {step === 3 && <Review s={s} job={job} go={go} consent={consent} setConsent={setConsent} slug={slug} lang={lang} setLang={setLang} cons={cons} setCons={setCons} />}
        </motion.div>
      </AnimatePresence>
      <div className="flex items-center gap-3 border-t border-line pt-4">
        {step > 0 && <Button type="button" variant="outline" className="h-11" onClick={() => go(step - 1)} data-testid="apply-back-btn"><ArrowLeft size={16} /> Back</Button>}
        {step < 3 ? (
          <Button type="button" className="ml-auto h-11 flex-1 sm:flex-none" style={{ backgroundColor: color }} onClick={next} data-testid="apply-next-btn">Continue <ArrowRight size={16} /></Button>
        ) : (
          <Button type="button" disabled={busy || !consent || !cons.age} className="ml-auto h-11 flex-1 sm:flex-none" style={{ backgroundColor: color }} onClick={submit} data-tour="pt-apply-submit-btn" data-testid="apply-submit-btn">
            {busy && <Loader2 size={16} className="animate-spin" />} Submit application
          </Button>
        )}
      </div>
    </section>
  );
}
